import React, { Component, ErrorInfo, ReactNode } from 'react';
import './ErrorBoundary.scss';

/**
 * Error Boundary to catch runtime errors and lazy-load (chunk) failures.
 * When a dynamic import fails (e.g. 404 after deploy), Suspense never resolves
 * and the user stays on the loading screen. This boundary catches the error
 * and shows a message with a reload option.
 */
interface Props {
  children: ReactNode;
  fallbackTitle?: string;
  fallbackMessage?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const isChunkError =
        this.state.error?.message?.includes('Loading chunk') ||
        this.state.error?.message?.includes('ChunkLoadError') ||
        this.state.error?.name === 'ChunkLoadError';
      const title = this.props.fallbackTitle ?? (isChunkError ? 'Échec du chargement' : 'Une erreur est survenue');
      const message =
        this.props.fallbackMessage ??
        (isChunkError
          ? 'La page n’a pas pu être chargée. Rechargez la page pour réessayer.'
          : 'Une erreur inattendue s’est produite. Rechargez la page pour réessayer.');

      return (
        <div className="error-boundary">
          <div className="error-boundary__content">
            <h2 className="error-boundary__title">{title}</h2>
            <p className="error-boundary__message">{message}</p>
            <button type="button" className="error-boundary__button" onClick={this.handleReload}>
              Recharger la page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
